/**
 * AccountMenu — the single shared account dropdown rendered by EVERY portal's
 * top pill (the admin shell + the PMS dashboard today). Pins the artboard menu
 * (`.design-incoming/admin-20260522/source/shell.jsx` AccountMenu): user card ·
 * Density (SegmentedControl + "saved to your preferences" hint) · Appearance
 * (Light / Dark / System) · a multi-portal "Switch portal" section · action
 * links · Sign out.
 *
 * Lives in `entities/` (presentational domain noun reused in 2+ places per
 * Hybrid Z / ADR frontend-0001) so the two portals can't drift — there is one
 * implementation, not a per-portal copy.
 *
 * **Presentational by design.** Preferences, portal entitlements, the current
 * portal, and the action-link destinations are all passed in by the host pill
 * (which owns `useUserPreferences()` + `useMe()`), so this component carries no
 * query-client / localStorage coupling and host tests stay simple.
 *
 * **Per-portal config via props:**
 *   - `currentPortal` — marks/omits the right row in the switcher
 *     (admin → 'Admin', PMS dashboard → 'PMS').
 *   - `actionLinks` — each portal supplies its own in-shell destinations
 *     (e.g. admin → `/admin/account`, dashboard → `/account`), so the links
 *     stay within that portal's chrome and enforcement.
 *
 * The portal switcher self-hides for single-portal / zero-portal users (so
 * those menus match the artboard, which ships no toggle). Closes on outside click
 * (mousedown); Escape + open state are owned by the host pill.
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Avatar, Icon } from '@sparta/ui-kit';
import {
  type ThemePreference,
  type DensityPreference,
} from '../../shared/hooks/useUserPreferences.js';
import { type MePortalId } from '../../shared/auth/use-me.js';
import { SegmentedControl, type SegmentedOption } from './SegmentedControl.js';
import {
  PortalSwitcher,
  hasPortalSwitch,
} from '../portal/PortalSwitcher.js';

type IconName = React.ComponentProps<typeof Icon>['name'];

export interface AccountMenuActionLink {
  readonly label: string;
  readonly icon: IconName;
  readonly to: string;
}

const DENSITY_OPTIONS: readonly SegmentedOption<DensityPreference>[] = [
  { id: 'lite', label: 'Lite' },
  { id: 'pro', label: 'Pro' },
];

const APPEARANCE_OPTIONS: readonly SegmentedOption<ThemePreference>[] = [
  { id: 'light', label: 'Light', icon: 'sun' },
  { id: 'dark', label: 'Dark', icon: 'moon' },
  { id: 'system', label: 'System', icon: 'monitor' },
];

export interface AccountMenuProps {
  readonly user: { id: string; email: string; name?: string };
  readonly theme: ThemePreference;
  readonly density: DensityPreference;
  readonly onThemeChange: (theme: ThemePreference) => void;
  readonly onDensityChange: (density: DensityPreference) => void;
  readonly portals: ReadonlyArray<MePortalId>;
  /** The portal the user is currently in (admin shell → 'Admin', PMS → 'PMS'). */
  readonly currentPortal: MePortalId;
  /** Per-portal action-link destinations (kept within that portal's chrome). */
  readonly actionLinks: readonly AccountMenuActionLink[];
  readonly onClose: () => void;
  /**
   * Sign-out handler. The host pill owns the flow (POST /auth/logout →
   * queryClient.clear() → navigateToLogin); this component fires it and reports
   * any rejection so a silent failure surfaces in Sentry.
   */
  readonly onSignOut?: () => void | Promise<void>;
  /**
   * SCRUM-15 reviewer fix #4: ref to the trigger element (the avatar
   * button) that toggles this menu. The outside-click handler skips
   * mousedown events whose target lives inside the trigger so the
   * trigger's own `onClick` handles the toggle without racing the
   * close-on-mousedown → reopen-on-click sequence. When omitted,
   * mousedown on the trigger closes the menu (and the trigger's click
   * reopens it, restoring the old race).
   */
  readonly triggerRef?: React.RefObject<HTMLElement>;
}

function Divider(): React.ReactElement {
  return <div aria-hidden="true" className="h-px my-1 bg-[var(--border-subtle)]" />;
}

function SectionLabel({
  children,
}: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[var(--fg-tertiary)]">
      {children}
    </div>
  );
}

export function AccountMenu({
  user,
  theme,
  density,
  onThemeChange,
  onDensityChange,
  portals,
  currentPortal,
  actionLinks,
  onClose,
  onSignOut,
  triggerRef,
}: AccountMenuProps): React.ReactElement {
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  // Outside-click closes the menu. SCRUM-15 reviewer fix #4 (2026-05-27):
  // the round-1 handler closed on any mousedown outside `menuRef`, which
  // included the avatar trigger itself. The trigger's own onClick fires
  // AFTER mousedown, so clicking the avatar to close an open menu would
  // close-then-reopen (the click toggle flipped the just-closed state back
  // to open). Excluding the trigger from the outside-click set lets the
  // trigger's onClick own the toggle cleanly. `triggerRef` is optional —
  // callers without a trigger (e.g. legacy hosts, tests) keep the old
  // behavior.
  React.useEffect(() => {
    const onMouseDown = (e: MouseEvent): void => {
      if (menuRef.current === null) return;
      if (!(e.target instanceof Node)) return;
      const insideMenu = menuRef.current.contains(e.target);
      const insideTrigger =
        triggerRef?.current !== null &&
        triggerRef?.current !== undefined &&
        triggerRef.current.contains(e.target);
      if (insideMenu || insideTrigger) return;
      onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onClose, triggerRef]);

  const displayName =
    user.name !== undefined && user.name.trim().length > 0 ? user.name : '—';
  const showSwitcher = hasPortalSwitch(portals);

  return (
    // `role="region"` (not `menu`): a preferences/disclosure surface, not a
    // WAI-ARIA menu of menuitems. `[box-shadow:var(--shadow-xl)]`
    // arbitrary-property form is permitted per the ADR-0003 allow-list.
    <div
      ref={menuRef}
      role="region"
      aria-label="Account menu"
      // eslint-disable-next-line @sparta/no-pixel-literal-arbitrary -- SCRUM-79: w-[300px] handoff panel width; no Tailwind default matches (w-72=288 / w-80=320).
      className="absolute right-0 top-[calc(100%+10px)] z-50 w-[300px] flex flex-col gap-2 p-3 bg-[var(--bg-surface)] border border-solid border-[var(--border-subtle)] rounded-xl [box-shadow:var(--shadow-xl)]"
    >
      {/* User card */}
      <div className="flex items-center gap-2.5 pt-2 px-2 pb-3 border-b border-solid border-[var(--border-subtle)]">
        <Avatar name={user.name} size={36} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[var(--fg-primary)] overflow-hidden text-ellipsis whitespace-nowrap">
            {displayName}
          </div>
          <div
            className="text-xs text-[var(--fg-tertiary)] overflow-hidden text-ellipsis whitespace-nowrap"
            title={user.email}
          >
            {user.email}
          </div>
        </div>
      </div>

      {/* Density */}
      <div className="px-2 py-1">
        <SectionLabel>Density</SectionLabel>
        <SegmentedControl
          ariaLabel="Density"
          uppercase
          value={density}
          onChange={onDensityChange}
          options={DENSITY_OPTIONS}
        />
        <div className="mt-1.5 text-[11px] text-[var(--fg-tertiary)]">
          Saved to your preferences. Applies across this portal.
        </div>
      </div>

      {/* Appearance */}
      <div className="px-2 py-1">
        <SectionLabel>Appearance</SectionLabel>
        <SegmentedControl
          ariaLabel="Appearance"
          value={theme}
          onChange={onThemeChange}
          options={APPEARANCE_OPTIONS}
        />
      </div>

      <Divider />

      {/* Switch portal — multi-portal users only (self-hides otherwise). */}
      {showSwitcher ? (
        <>
          <PortalSwitcher portals={portals} current={currentPortal} />
          <Divider />
        </>
      ) : null}

      {/* Action links — per-portal destinations (placeholders until real pages ship). */}
      {actionLinks.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          onClick={onClose}
          className="flex items-center gap-3 py-2.5 px-2 rounded-md no-underline text-sm text-[var(--fg-primary)] hover:bg-[var(--bg-hover)]"
        >
          <Icon name={item.icon} size={16} aria-hidden />
          <span className="flex-1">{item.label}</span>
        </Link>
      ))}

      <Divider />

      {/* Hand-rolled <button>: a full-width left-aligned destructive menu row.
          Neither kit <Button> nor <IconBtn> matches this layout. */}
      {/* eslint-disable-next-line @sparta/no-hand-rolled-button */}
      <button
        type="button"
        onClick={() => {
          // Fire-and-forget from the caller's view (the tree is about to
          // unmount on navigateToLogin), but report rejections so silent
          // logout failures surface in Sentry.
          if (onSignOut === undefined) return;
          onSignOut()?.catch((error: unknown) => {
            Sentry.captureException(error, {
              tags: { surface: 'account-menu-signout' },
            });
          });
        }}
        className="flex items-center gap-3 py-2.5 px-2 bg-transparent border-none rounded-md text-[var(--fg-danger)] text-sm font-medium cursor-pointer text-left hover:bg-[var(--bg-hover)]"
      >
        <Icon name="logout" size={16} aria-hidden />
        <span className="flex-1">Sign out</span>
      </button>
    </div>
  );
}
